import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SkeletonStat } from '../../components/ui/skeletons';
import { Link, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { track } from '../../services/telemetry';
import { scheduleService } from '../../services/scheduleService';
import { inspectionService } from '../../services/inspectionService';
import { UnsentReports, useUnsentReports } from './components/UnsentReports';
import { splitToday } from './todaySchedule';
import { AttentionPanel } from './components/AttentionPanel';
import { DismissButton } from './components/DismissButton';
import { RainEventChip } from './components/RainEventChip';
import { DirectionsLink } from '../../components/DirectionsLink';
import { isRainVisit } from './rainVisit';
import { api } from '../../services/api';
import { offlineStorage } from '../../services/offlineStorage';
import { isUnfinished } from './submittedDraft';
import { useAuthStore } from '../../stores/authStore';
import { format } from 'date-fns';
import {
  AlertTriangle,
  Building2,
  Calendar,
  Check,
  ChevronRight,
  CloudRain,
  FileText,
  MapPin,
  Play,
  Users,
} from 'lucide-react';

export function LiteDashboard() {
  const userId = useAuthStore((s) => s.user?.id);

  // Usage telemetry: the Lite landing after login (once per mount).
  useEffect(() => {
    track('lite_login_landing');
  }, []);

  // All four buckets (My / Group / Overdue / Rainfall) come from the single
  // access resolver so these counters can never drift from the Sites landing
  // sections they tap into.
  const { data: access, isLoading } = useQuery({
    queryKey: ['my-access'],
    queryFn: () => scheduleService.getMyAccess(),
  });

  // Fetch weekly plan for today's schedule
  const today = new Date();
  const weekDate = format(today, 'yyyy-MM-dd');
  const todayDayName = format(today, 'EEEE');

  const { data: weeklyPlan, isLoading: scheduleLoading } = useQuery({
    queryKey: ['weekly-plan', weekDate],
    queryFn: () => scheduleService.getWeeklyPlan(weekDate),
  });

  // Get today's sites from the weekly plan
  const todayAll =
    weeklyPlan?.days?.find((d) => d.day_of_week === todayDayName)?.sites || [];
  const { remaining: todaySites, done: todayDone } = splitToday(todayAll);
  const [showDone, setShowDone] = useState(false);

  // Unsubmitted inspections (Jim 2026-07-30 P0): drafts live only in this
  // device's IndexedDB, so testers who answered questions but never hit
  // Review Report had nothing surfacing that fact. Count this
  // user's non-stale drafts with at least one answer.
  const { data: unsentReports } = useUnsentReports(userId);
  // Jim 2026-10-08: a finished inspection lands here now; show the note the
  // flow passes along (offline save, report sent), once per arrival.
  const location = useLocation();
  const { toast: arrivalToast } = useToast();
  const arrivalMessageRef = useRef<string | null>(null);
  useEffect(() => {
    const message = (location.state as { message?: string } | null)?.message;
    if (message && arrivalMessageRef.current !== message) {
      arrivalMessageRef.current = message;
      arrivalToast({ title: message });
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const { data: unsubmittedDrafts } = useQuery({
    queryKey: ['lite-unsubmitted-drafts', userId],
    queryFn: async () => {
      await offlineStorage.init();
      const drafts = await offlineStorage.getDraftsForUser(userId!);
      // Answers kept after Complete (for Edit) are filed, not unfinished.
      return drafts.filter((d) => isUnfinished(d));
    },
    enabled: !!userId,
  });

  const queryClient = useQueryClient();

  // Needs report (audit 2026-09-04, C1): an inspection whose create reached
  // the server but whose report step never ran (a replayed offline submit,
  // a crash mid-flow) sits as a server DRAFT that no Lite view listed. Show
  // this inspector's drafts with a one-tap finish: freeze the saved
  // signature, then generate the report (which also advances it to
  // "submitted" so it lands in Unsent Reports).
  const { data: needsReport } = useQuery({
    queryKey: ['lite-needs-report', userId],
    queryFn: async () => {
      const page = await inspectionService.listInspections({
        status: 'draft',
        inspector_id: userId,
        size: 20,
        skip_count: true,
      });
      const items = page.items as unknown as Array<{
        id: string;
        site_id: string;
        site_name?: string | null;
        conducted_date?: string | null;
        scheduled_date?: string | null;
        created_at?: string | null;
      }>;
      // Only recent drafts are "mine to finish"; older ones are admin or
      // legacy leftovers that would bury the real ones.
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return items.filter((i) => {
        const when = i.conducted_date || i.scheduled_date || i.created_at;
        return !when || new Date(when).getTime() >= cutoff;
      });
    },
    enabled: !!userId,
  });
  // Rows the unsent queue is showing (a receipt stays after the server
  // list empties); keeps the attention panel open for it.
  const [unsentRows, setUnsentRows] = useState(0);
  const [finishFeedback, setFinishFeedback] = useState<{
    id: string;
    ok: boolean;
    text: string;
  } | null>(null);
  const finishReportMutation = useMutation({
    mutationFn: async (inspectionId: string) => {
      await api
        .post(`/inspections/${inspectionId}/use-saved-signature`)
        .catch(() => undefined); // the generator falls back to no signature
      return inspectionService.generateReport(inspectionId);
    },
    onSuccess: (_data, inspectionId) => {
      setFinishFeedback({
        id: inspectionId,
        ok: true,
        text: 'Report generated — it is now under Unsent Reports.',
      });
      queryClient.invalidateQueries({ queryKey: ['lite-needs-report'] });
      queryClient.invalidateQueries({ queryKey: ['lite-unsent-reports'] });
    },
    onError: (_err, inspectionId) => {
      setFinishFeedback({
        id: inspectionId,
        ok: false,
        text: 'Could not generate the report — try again or contact your admin.',
      });
    },
  });

  // JIM-0919-11 (Jim 2026-09-19): opening a site "just to look" leaves a
  // draft that nags from this panel — "I need like a delete". Dismissing an
  // unsubmitted row clears the draft saved on this device; deleting a
  // needs-a-report row discards the server draft (own drafts only, nothing
  // with a report). Unsent reports keep their Send / Stop actions.
  const dismissLocalDraftMutation = useMutation({
    mutationFn: async (siteId: string) => {
      await offlineStorage.init();
      await offlineStorage.clearDraft(siteId, userId!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['lite-unsubmitted-drafts', userId],
      });
    },
  });
  const discardServerDraftMutation = useMutation({
    mutationFn: (inspectionId: string) =>
      inspectionService.discardDraftInspection(inspectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lite-needs-report'] });
    },
    onError: (_err, inspectionId) => {
      setFinishFeedback({
        id: inspectionId,
        ok: false,
        text: 'Could not delete this draft — try again or contact your admin.',
      });
    },
  });

  // Resolve site names for draft rows from the access buckets already loaded.
  const siteNameById = new Map<string, string>();
  [
    ...(access?.my_sites ?? []),
    ...(access?.group_sites ?? []),
    ...(access?.overdue ?? []),
    ...(access?.rainfall ?? []),
  ].forEach((s) => siteNameById.set(s.id, s.name));

  const myCount = access?.my_sites.length ?? 0;
  const groupCount = access?.group_sites.length ?? 0;
  const overdueCount = access?.overdue.length ?? 0;
  const rainfallCount = access?.rainfall.length ?? 0;

  // One row for both lists: still-to-do (Start) and done (Completed, folded).
  const todayRow = (site: (typeof todayAll)[number]) => (
    // JIM-1006-13 (Jim 2026-10-06): the row opens the site; only the
    // button starts an inspection, so a tap on the name no longer
    // leaves a draft to resume.
    <div
      key={site.inspection_id}
      data-testid={site.completed ? 'today-completed' : 'today-todo'}
      className={`px-5 py-3 flex items-center gap-3 ${site.completed ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
    >
      <Link
        to={`/lite/sites/${site.site_id}`}
        className="flex-1 min-w-0 flex items-center gap-2 -mx-2 px-2 py-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors active:bg-slate-100 dark:active:bg-slate-700"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
            {site.site_name}
            {isRainVisit(site.inspection_type) && <RainEventChip />}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {site.client_name && `${site.client_name} · `}
            {site.city}
            {site.city && site.state ? ', ' : ''}
            {site.state}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </Link>
      <DirectionsLink
        latitude={site.latitude}
        longitude={site.longitude}
        compact
      />
      {site.completed ? (
        // JIM-1023-14 (Jim 10/23): "a very clear indication… the
        // whole lettering changes" — the row stays, reads Completed,
        // and sits at the bottom; the site link still opens it.
        <span
          className="flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex-shrink-0"
          aria-label={`Completed: ${site.site_name}`}
        >
          <Check className="w-3.5 h-3.5" />
          Completed
        </span>
      ) : (
        <Link
          to={`/lite/inspect/${site.site_id}${isRainVisit(site.inspection_type) ? '?rain=1' : ''}`}
          aria-label={`Start inspection at ${site.site_name}`}
          className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg flex-shrink-0"
        >
          <Play className="w-3.5 h-3.5" />
          Start
        </Link>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          Dashboard
        </h1>
      </div>

      {/* Everything that needs the inspector's action, folded into one block
          (Chris 2026-09-16): unsubmitted drafts on this device, server drafts
          with no report, and reports not yet sent. */}
      <AttentionPanel
        keepOpen={unsentRows > 0}
        items={[
          {
            key: 'unsubmitted',
            count: unsubmittedDrafts?.length ?? 0,
            label: ['inspection to resume', 'inspections to resume'],
            tone: 'amber',
          },
          {
            key: 'needs-report',
            count: needsReport?.length ?? 0,
            label: ['needs a report', 'need a report'],
            tone: 'amber',
          },
          {
            key: 'unsent',
            count: unsentReports?.length ?? 0,
            label: ['unsent report', 'unsent reports'],
            tone: 'sky',
          },
        ]}
      >
        {/* Jim + Chris 2026-10-08: notifications live in the top-bar bell
            only; the dashboard tile is gone to give the page room. */}
        {/* Unsubmitted inspections banner — answered on this device but never
          submitted. Tapping a row reopens the flow, which offers to resume
          the saved draft. */}
        {unsubmittedDrafts && unsubmittedDrafts.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl shadow-soft overflow-hidden">
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  You have {unsubmittedDrafts.length} unfinished inspection
                  {unsubmittedDrafts.length === 1 ? '' : 's'}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  Your answers are saved on this device. Resume to finish one,
                  or tap the X to clear one started by mistake.
                </p>
              </div>
            </div>
            <div className="divide-y divide-amber-200 dark:divide-amber-800 border-t border-amber-200 dark:border-amber-800">
              {unsubmittedDrafts.map((draft) => {
                const name =
                  siteNameById.get(draft.siteId) || 'Inspection in progress';
                return (
                  <div
                    key={draft.key}
                    className="flex items-center gap-2 pl-4 pr-2 py-1.5"
                  >
                    <Link
                      to={`/lite/inspect/${draft.siteId}`}
                      className="flex-1 min-w-0 flex items-center justify-between py-1.5 pr-2 rounded-lg hover:bg-amber-100/60 dark:hover:bg-amber-900/30 transition-colors active:bg-amber-100 dark:active:bg-amber-900/40"
                    >
                      <span className="text-sm font-medium text-amber-900 dark:text-amber-100 truncate">
                        {name}
                      </span>
                      <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300 flex-shrink-0">
                        Resume
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <DismissButton
                      ariaLabel={`Dismiss draft: ${name}`}
                      busy={
                        dismissLocalDraftMutation.isPending &&
                        dismissLocalDraftMutation.variables === draft.siteId
                      }
                      onConfirm={() =>
                        dismissLocalDraftMutation.mutate(draft.siteId)
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Needs report — server drafts from this inspector with no report yet */}
        {needsReport && needsReport.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl shadow-soft overflow-hidden">
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  {needsReport.length} inspection
                  {needsReport.length === 1 ? '' : 's'} need
                  {needsReport.length === 1 ? 's' : ''} a report
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  These answers reached the server but the report was never
                  generated. Generate it here, then send it from Unsent Reports.
                </p>
              </div>
            </div>
            <div className="divide-y divide-amber-200 dark:divide-amber-800 border-t border-amber-200 dark:border-amber-800">
              {needsReport.map((insp) => {
                const when = insp.conducted_date || insp.created_at;
                const isBusy =
                  finishReportMutation.isPending &&
                  finishReportMutation.variables === insp.id;
                return (
                  <div key={insp.id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100 truncate">
                          {insp.site_name ||
                            siteNameById.get(insp.site_id) ||
                            'Inspection'}
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          {when
                            ? format(new Date(when), 'MMM d, yyyy')
                            : 'Date unknown'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => finishReportMutation.mutate(insp.id)}
                          disabled={isBusy}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-xs font-semibold transition-colors flex-shrink-0"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          {isBusy ? 'Generating\u2026' : 'Generate report'}
                        </button>
                        <DismissButton
                          label="Delete"
                          ariaLabel={`Delete draft: ${
                            insp.site_name ||
                            siteNameById.get(insp.site_id) ||
                            'Inspection'
                          }`}
                          busy={
                            discardServerDraftMutation.isPending &&
                            discardServerDraftMutation.variables === insp.id
                          }
                          onConfirm={() =>
                            discardServerDraftMutation.mutate(insp.id)
                          }
                        />
                      </div>
                    </div>
                    {finishFeedback?.id === insp.id && (
                      <p
                        className={`mt-2 text-xs font-medium ${
                          finishFeedback.ok
                            ? 'text-green-700 dark:text-green-400'
                            : 'text-red-700 dark:text-red-400'
                        }`}
                      >
                        {finishFeedback.text}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <UnsentReports userId={userId} onRowsChange={setUnsentRows} />
      </AttentionPanel>

      {/* Start New Inspection — one compact row (Jim 2026-10-08: "reduce
          the size of that blue start inspection button"). */}
      <Link
        to="/lite/start-inspection"
        className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-md px-4 py-3 text-white hover:from-primary-600 hover:to-primary-700 transition-all active:scale-[0.98] flex items-center gap-3"
      >
        <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-5 h-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold">
            Start New Inspection
          </span>
          <span className="block text-primary-100 text-xs">
            Use GPS to detect your current site
          </span>
        </span>
        <ChevronRight className="w-5 h-5 text-white/80 flex-shrink-0" />
      </Link>

      {/* Summary Cards — each taps into the Sites landing and scrolls to its
          section. All four counts come from the same resolver as those sections. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* My Sites — assigned to me */}
        <Link
          to="/lite/sites#my-sites"
          className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow active:bg-slate-50 dark:active:bg-slate-700 min-h-[100px]"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                My Sites
              </p>
              <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                {isLoading ? <SkeletonStat /> : myCount}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </Link>

        {/* Group Sites — in my group(s), available to claim */}
        <Link
          to="/lite/sites#group-sites"
          className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow active:bg-slate-50 dark:active:bg-slate-700 min-h-[100px]"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Group Sites
              </p>
              <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                {isLoading ? <SkeletonStat /> : groupCount}
              </p>
              <p className="text-2xs text-slate-400 dark:text-slate-500 mt-0.5">
                available to claim
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            </div>
          </div>
        </Link>

        {/* Overdue — day-based; lands on the Overdue section of the landing */}
        <Link
          to="/lite/sites#overdue"
          className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow active:bg-slate-50 dark:active:bg-slate-700 min-h-[100px]"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Overdue Inspections
              </p>
              <p className="text-3xl font-bold text-danger-600 dark:text-danger-400 mt-1">
                {isLoading ? <SkeletonStat /> : overdueCount}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-danger-100 dark:bg-danger-900/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-danger-600 dark:text-danger-400" />
            </div>
          </div>
        </Link>

        {/* Rainfall — my sites with a rain event inspection still to do */}
        <Link
          to="/lite/sites#rainfall"
          className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow active:bg-slate-50 dark:active:bg-slate-700 min-h-[100px]"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Rain Event Inspections
              </p>
              <p className="text-3xl font-bold text-sky-600 dark:text-sky-400 mt-1">
                {isLoading ? <SkeletonStat /> : rainfallCount}
              </p>
              <p className="text-2xs text-slate-400 dark:text-slate-500 mt-0.5">
                still to do
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
              <CloudRain className="w-5 h-5 text-sky-600 dark:text-sky-400" />
            </div>
          </div>
        </Link>
      </div>

      {/* Today's Schedule */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Today&apos;s Schedule
            </h2>
            {todaySites.length > 0 && (
              <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-1.5 py-0.5 rounded-full">
                {todaySites.length}
              </span>
            )}
          </div>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {format(today, 'EEEE, MMM d')}
          </span>
        </div>
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {scheduleLoading ? (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading schedule...
              </p>
            </div>
          ) : todaySites.length > 0 ? (
            todaySites.map(todayRow)
          ) : (
            <div className="px-5 py-8 text-center" data-testid="today-empty">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {todayDone.length > 0
                  ? 'All done for today'
                  : 'No sites scheduled for today'}
              </p>
            </div>
          )}
          {todayDone.length > 0 && (
            <div data-testid="today-done">
              <button
                type="button"
                onClick={() => setShowDone((v) => !v)}
                aria-expanded={showDone}
                className="w-full px-5 py-3 flex items-center justify-between text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/40"
              >
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  Done today · {todayDone.length}
                </span>
                <ChevronRight
                  className={`w-4 h-4 text-slate-400 transition-transform ${showDone ? 'rotate-90' : ''}`}
                />
              </button>
              {showDone && todayDone.map(todayRow)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
